import React, { FC } from 'react';
import Select, { components, StylesConfig } from 'react-select';
// components
import DateInput from './dateInput';
// style
import './index.scss';
// localization
import strings from '../../../localization';

interface InputProps {
  label: string;
  input: string | { day: string; month: string; year: string };
  /** Renders instead of a text input when the field value is custom React content (e.g. color swatches). */
  inputContent?: React.ReactNode;
  id: string;
  disabled: boolean;
  name?: string;
  type?: 'full' | 'centered' | 'standart';
  success?: boolean;
  select?: boolean;
  isSearchable?: boolean;
  options?: Array<{ key: string | number; value: string | number; label: string | number }>;
  handleChange?: any;
  selectValue?: string;
  multipleValue?: Array<{ fieldName: string; fieldValue: string }>;
  placeholder?: string;
  icon?: string;
  required?: boolean;
  inputType?: 'text' | 'number' | 'password' | 'email' | 'date' | 'tel';
  min?: number;
  max?: number;
  readOnly?: boolean;
  autoComplete?: string;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
}

// Maps react-select surfaces to exam UI grays so default white control/menu styles do not win over SCSS.
const examEnvironmentSelectStyles: StylesConfig<unknown, false> = {
  control: (base, state) => ({
    ...base,
    backgroundColor: state.isDisabled ? 'var(--shell-input-bg, var(--env-surface-input))' : 'var(--env-surface-input)',
    borderColor: state.isFocused
      ? 'var(--shell-input-focus-border, var(--env-surface-input-border))'
      : 'var(--shell-input-border, var(--env-surface-input-border))',
    borderWidth: 1,
    boxShadow: state.isFocused ? 'var(--shell-input-shadow-focus, none)' : 'var(--shell-input-shadow, none)',
    minHeight: 'var(--shell-control-height, 36px)',
    height: 'var(--shell-control-height, 36px)',
    borderRadius: 6,
    cursor: state.isDisabled ? 'not-allowed' : 'default',
    opacity: state.isDisabled ? 0.85 : 1,
    ':hover': {
      borderColor: state.isFocused
        ? 'var(--shell-input-focus-border, var(--env-surface-input-border))'
        : 'var(--shell-input-border, var(--env-surface-input-border))',
    },
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: 'var(--env-surface-input)',
    border: '1px solid var(--env-surface-input-border)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
    zIndex: 10,
  }),
  menuList: (base) => ({
    ...base,
    backgroundColor: 'var(--env-surface-input)',
    padding: '4px 0',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused || state.isSelected ? 'var(--env-secondary-btn-hover-bg)' : 'transparent',
    color: 'var(--env-text-on-input)',
    cursor: 'pointer',
    ':active': {
      backgroundColor: 'var(--env-secondary-btn-hover-bg)',
    },
  }),
  singleValue: (base) => ({
    ...base,
    color: 'var(--env-text-on-input)',
  }),
  placeholder: (base) => ({
    ...base,
    color: 'var(--env-placeholder-on-input)',
    fontFamily: 'var(--shell-placeholder-font, inherit)',
    fontSize: 'var(--shell-placeholder-size, 13px)',
    lineHeight: 'inherit',
    fontWeight: 'var(--shell-placeholder-weight, 300)',
  }),
  input: (base) => ({
    ...base,
    color: 'var(--env-text-on-input)',
  }),
  valueContainer: (base) => ({
    ...base,
    backgroundColor: 'transparent',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    backgroundColor: 'transparent',
  }),
};

// @ts-ignore
const Input: FC<InputProps> = ({
  label,
  input,
  inputContent,
  id,
  disabled,
  type,
  name,
  success,
  select,
  isSearchable,
  options,
  handleChange,
  selectValue,
  multipleValue,
  placeholder,
  icon,
  required,
  inputType,
  min,
  max,
  readOnly,
  autoComplete = 'off',
  onFocus,
  onClick,
}: InputProps) => {
  const { Option } = components;
  const isInputReadOnly = readOnly === true;
  const inputAutoComplete = autoComplete;

  // custom select chosen value with icon
  const SingleValue = (props: any) => {
    return (
      <components.SingleValue {...props}>
        {props.data.chipLabel && <div className={`icon icon-${props.data.icon}`} />}
        {props.data.chipLabel ? props.data.chipLabel : props.data.label}
      </components.SingleValue>
    );
  };
  // custom select option with icon
  const IconOption = (props: any) => (
    <Option {...props}>
      {props.data.chipLabel && <div className={`icon icon-${props.data.icon}`} />}
      {props.data.label}
    </Option>
  );
  return (
    <div
      className={`input-container${type ? ` input-container_${type}` : ''}${
        success ? ' success' : success !== undefined && !success ? ' error' : ''
      }`}
    >
      {select && options ? (
        <div className="label">
          {label && (
            <span id={`${id}-label`} className={`label__name${handleChange || type ? '' : ' disabled'}`}>
              {label}
            </span>
          )}
          <Select
            components={{
              DropdownIndicator: () => null,
              IndicatorSeparator: () => null,
              SingleValue,
              Option: IconOption,
            }}
            styles={examEnvironmentSelectStyles}
            className={`select ${id}`}
            classNamePrefix="select"
            menuShouldScrollIntoView={true}
            menuPlacement="auto"
            inputId={id}
            name={id}
            aria-labelledby={label ? `${id}-label` : undefined}
            value={input ? options.find((item) => item.value.toString() === String(input)) : null}
            options={options}
            placeholder={placeholder ? placeholder : strings.distancePage.select.chooseOption}
            isDisabled={disabled}
            isSearchable={isSearchable}
            onChange={handleChange}
            defaultValue={options.find((item) => item.value == selectValue)}
          />
        </div>
      ) : (
        <label className="label" htmlFor={id}>
          {label && <span className={`label__name${handleChange || type ? '' : ' disabled'}`}>{label}</span>}
          {multipleValue ? (
            <div id={id} className="input input_multiple">
              {multipleValue.map((item, index) => (
                <div key={index}>
                  <span className="input__field-name">{item.fieldName}</span>:
                  <span className="input__field-value">{item.fieldValue}</span>
                </div>
              ))}
            </div>
          ) : // date input
          inputType === 'date' && typeof input !== 'string' ? (
            <DateInput
              id={id}
              handleChange={handleChange}
              value={input}
              disabled={disabled}
              required={required ? required : false}
            />
          ) : inputContent ? (
            <div className="input__outer">
              <div
                id={id}
                className={`input input_readonly-content${handleChange ? '' : ' disabled'}`}
                aria-labelledby={label ? `${id}-label` : undefined}
              >
                {inputContent}
              </div>
              {icon && <div className={`icon icon-${icon}`} />}
            </div>
          ) : (
            typeof input === 'string' && (
              // usual input
              <div className="input__outer">
                <input
                  name={name}
                  id={id}
                  className={`input${handleChange ? '' : ' disabled'}`}
                  type={inputType ? inputType : 'text'}
                  value={input}
                  disabled={disabled}
                  onChange={handleChange}
                  placeholder={placeholder}
                  required={required}
                  minLength={min}
                  maxLength={max}
                  readOnly={isInputReadOnly}
                  autoComplete={inputAutoComplete}
                  onFocus={onFocus}
                  onClick={onClick}
                />
                {icon && <div className={`icon icon-${icon}`} />}
              </div>
            )
          )}
        </label>
      )}
    </div>
  );
};

export default Input;
